import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { monthRange, type TrendSeries } from '../../audit/analytics';
import { scorePct } from '../scoreText';
import { isPhoneWidth } from '../Pager';
import { plural } from '../plural';

/**
 * Score trend: one 2px line per series over months, y fixed at 0–100.
 * Hand-rolled SVG (no chart library). Colors follow the series ENTITY in a
 * fixed categorical order (the caller passes them sorted by name, so a
 * filter never repaints the survivors). Crosshair snaps to the nearest
 * month and the tooltip lists every series; a table view carries the same
 * numbers (the palette's lighter slots need it for contrast).
 */

/** Validated categorical order (dataviz reference palette, light), all 8 slots. */
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'] as const;
export const MAX_SERIES = SERIES_COLORS.length;

/** Drawn at the container's real width (text stays 11px on a phone); this is the fallback. */
const DEFAULT_W = 720;
const H = 260;
// right: room for half of the last month label ("Oct 2026" at 11 px ≈ 50 px
// wide), centred on the last point — it was clipped to "Oct 202" (DM run F12).
const PAD = { top: 16, right: 30, bottom: 32, left: 40 };

/** "Oct 2026" — "Oct 26" read as a day (DM exploration, 2026-10-19). */
const monthLabel = (m: string) => {
  const [y, mo] = m.split('-').map(Number) as [number, number];
  return new Date(y, mo - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

export function TrendChart({
  series,
  title,
  colorKeys,
}: {
  series: readonly TrendSeries[];
  title: string;
  /**
   * Every key that COULD appear, in a fixed order: a series' color comes from
   * its place here, so filtering a series out never repaints the others.
   */
  colorKeys: readonly string[];
}) {
  // Never cycle: callers keep to MAX_SERIES (else they show one combined line).
  const color = (key: string) => SERIES_COLORS[colorKeys.indexOf(key)] ?? '#898781';
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  // Phones read the table as a card per month: newest first, so the latest month isn't 2,000 px down (UI-6 review).
  const [newestFirst] = useState(isPhoneWidth);
  const svgRef = useRef<SVGSVGElement>(null);
  const [W, setW] = useState(DEFAULT_W);
  // Callback ref: observes whichever plot element is mounted (it unmounts for
  // the table view and for the empty state), so the width never goes stale.
  const roRef = useRef<ResizeObserver | null>(null);
  const plotRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry?.contentRect.width ?? 0);
      if (w > 0) setW(Math.max(280, w));
    });
    ro.observe(el);
    roRef.current = ro;
  }, []);

  const months = useMemo(() => {
    const all = series.flatMap((s) => s.points.map((p) => p.month)).sort();
    return monthRange(all[0] ?? '', all.at(-1) ?? '');
  }, [series]);
  // A filter can shorten the month list under a held crosshair. Keyed on the
  // months themselves, not the array, so a parent re-render keeps the tap.
  const monthsKey = months.join('|');
  useEffect(() => setHover(null), [monthsKey]);

  if (series.length === 0 || months.length === 0) {
    return <p className="muted">No scored audits in this period yet.</p>;
  }

  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (months.length === 1 ? plotW / 2 : (i / (months.length - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - (v / 100) * plotH;
  // About one month label per 70px, so labels never collide.
  const labelEvery = Math.max(1, Math.ceil(months.length / Math.max(2, Math.floor(plotW / 70))));
  const valueAt = (s: TrendSeries, month: string) => s.points.find((p) => p.month === month);
  const hoverMonth = hover !== null ? months[hover] : undefined;

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0;
    for (let i = 1; i < months.length; i++) if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i;
    setHover(best);
  };

  return (
    <figure className="viz-root viz-trend">
      <div className="viz-head">
        <figcaption>{title}</figcaption>
        <button type="button" className="btn btn-small" onClick={() => setAsTable((v) => !v)}>
          {asTable ? 'Show as chart' : 'Show as table'}
        </button>
      </div>
      {series.length > 1 && (
        <ul className="viz-legend" aria-label="Legend">
          {series.map((s) => (
            <li key={s.key}>
              <svg width="18" height="8" aria-hidden="true">
                <line x1="1" y1="4" x2="17" y2="4" stroke={color(s.key)} strokeWidth="2" strokeLinecap="round" />
              </svg>
              {s.label}
            </li>
          ))}
        </ul>
      )}
      {asTable ? (
        // Phones: a card per month with each line's value labelled (UI review V50: 7 columns in a 317 px box).
        <div className="table-wrap viz-table board-wrap">
          <table className="board-cards" role="table">
            <thead>
              <tr role="row">
                <th role="columnheader">Month</th>
                {series.map((s) => (
                  <th key={s.key} role="columnheader">{s.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(newestFirst ? [...months].reverse() : months).map((m) => (
                <tr key={m} role="row">
                  <td role="cell" className="card-full card-title">{monthLabel(m)}</td>
                  {series.map((s) => {
                    const p = valueAt(s, m);
                    return (
                      <td key={s.key} role="cell" data-label={s.label}>
                        {p ? scorePct(p.avg) : <span className="muted">—</span>}
                        {p && <span className="muted"> ({plural(p.count, 'audit')})</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="viz-plot" ref={plotRef}>
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={`${title}. Use “Show as table” for the numbers.`}
            onPointerMove={onMove}
            // Touch: a tap shows that month; it stays until the next tap.
            onPointerDown={onMove}
            onPointerLeave={(e) => {
              if (e.pointerType !== 'touch') setHover(null); // mouse and hovering pens
            }}
          >
            {[0, 25, 50, 75, 100].map((t) => (
              <g key={t}>
                <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} className={t === 0 ? 'viz-axis' : 'viz-grid'} />
                <text x={PAD.left - 6} y={y(t)} className="viz-tick" textAnchor="end" dominantBaseline="middle">
                  {t}
                </text>
              </g>
            ))}
            {months.map((m, i) =>
              // The last month always gets a label; a regular one too close
              // to it is dropped so the two never overlap.
              i === months.length - 1 || (i % labelEvery === 0 && months.length - 1 - i >= labelEvery) ? (
                <text key={m} x={x(i)} y={H - 10} className="viz-tick" textAnchor="middle">
                  {monthLabel(m)}
                </text>
              ) : null,
            )}
            {hover !== null && hoverMonth && <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} className="viz-crosshair" />}
            {series.map((s) => {
              // Break the line at months with no audits (a gap, not a slope).
              const segments: string[][] = [];
              let current: string[] = [];
              months.forEach((m, i) => {
                const p = valueAt(s, m);
                if (p) current.push(`${x(i).toFixed(1)},${y(p.avg).toFixed(1)}`);
                else if (current.length) {
                  segments.push(current);
                  current = [];
                }
              });
              if (current.length) segments.push(current);
              return (
                <g key={s.key} stroke={color(s.key)} fill={color(s.key)}>
                  {segments.map((seg, k) =>
                    seg.length > 1 ? (
                      <polyline key={k} points={seg.join(' ')} fill="none" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                    ) : null,
                  )}
                  {months.map((m, i) => {
                    const p = valueAt(s, m);
                    // Markers only where a month stands alone, or under the crosshair.
                    const alone = p && !valueAt(s, months[i - 1] ?? '') && !valueAt(s, months[i + 1] ?? '');
                    return p && (alone || hover === i) ? (
                      <circle key={m} cx={x(i)} cy={y(p.avg)} r="4" className="viz-dot" />
                    ) : null;
                  })}
                </g>
              );
            })}
          </svg>
          {hover !== null && hoverMonth && (
            <div
              className="viz-tooltip"
              // Near an edge, anchor the tooltip's side instead of its middle
              // so it never hangs off the plot (or the phone screen).
              style={
                x(hover) / W < 0.25
                  ? { left: `${(x(hover) / W) * 100}%`, transform: 'translateX(0)' }
                  : x(hover) / W > 0.75
                    ? { left: `${(x(hover) / W) * 100}%`, transform: 'translateX(-100%)' }
                    : { left: `${(x(hover) / W) * 100}%` }
              }
            >
              <div className="viz-tooltip-head">{monthLabel(hoverMonth)}</div>
              {series.map((s) => {
                const p = valueAt(s, hoverMonth);
                return (
                  <div key={s.key} className="viz-tooltip-row">
                    <svg width="12" height="6" aria-hidden="true">
                      <line x1="1" y1="3" x2="11" y2="3" stroke={color(s.key)} strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {p ? <strong>{scorePct(p.avg)}</strong> : <span className="muted">no audits</span>}
                    <span className="muted">
                      {s.label}
                      {p && ` · ${p.count} audit${p.count === 1 ? '' : 's'}`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </figure>
  );
}
